import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import { getDefaultLang, LangEnum, LangType } from '../../helpers/lang';
import { tableSettingsKey } from '../../helpers/local-storage';
import i18n from '../../i18n/i18n';
import { SortType } from '../../types/props';

dayjs.extend(isBetween);

export const sortKey = 'sort';

export const dishCategoryKey = 'category';
export const dishSectionKey = 'section';
export const dishNameKey = 'dishName';
export const dishPhotoKey = 'photo';
export const dishMenuKey = 'menuName';
export const dishCostKey = 'cost';
export const dishAvailableKey = 'available';
export const countKey = 'count';
export const sectionNameKey = 'sectionName';
export const categoryNameKey = 'categoryName';

const isEmpty = (val?: number | string | null) =>
  val === undefined || val === null || val === 0 || val === '';

export const sortByLangValue = (a?: LangType, b?: LangType, dir?: SortType) => {
  const lang = i18n.language as LangEnum;
  const aVal = a?.[lang];
  const bVal = b?.[lang];

  if (isEmpty(aVal) && isEmpty(bVal)) return 0;
  if (isEmpty(aVal)) return dir === 'asc' ? 1 : -1;
  if (isEmpty(bVal)) return dir === 'asc' ? -1 : 1;

  return dir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
};

export const sortByNumber = (a?: number | null, b?: number | null, direction?: SortType) => {
  if (isEmpty(a) && isEmpty(b)) return 0;
  if (isEmpty(a)) return direction === 'asc' ? 1 : -1;
  if (isEmpty(b)) return direction === 'asc' ? -1 : 1;

  return direction === 'asc' ? a - b : b - a;
};

export const sortByName = (a?: string | null, b?: string | null, direction?: SortType) => {
  if (isEmpty(a) && isEmpty(b)) return 0;
  if (isEmpty(a)) return direction === 'asc' ? 1 : -1;
  if (isEmpty(b)) return direction === 'asc' ? -1 : 1;

  return direction === 'asc'
    ? a.localeCompare(b, 'ru', { sensitivity: 'base' })
    : b.localeCompare(a, 'ru', { sensitivity: 'base' });
};

export const sortByTime = (a?: string | null, b?: string | null, direction?: SortType) => {
  if (!a && !b) return 0;
  const dateA = dayjs(a);
  const dateB = dayjs(b);
  const timeA = dateA.get('hour') * 60 + dateA.get('minute');
  const timeB = dateB.get('hour') * 60 + dateB.get('minute');
  if (direction === 'desc') return timeA - timeB;
  if (direction === 'asc') return timeB - timeA;
  return 0;
};

export const sortByCost = (a?: number | null, b?: number | null, direction?: SortType) => {
  if (!a && !b) return 0;
  if (!a) return direction === 'desc' ? -1 : 1;
  if (!b) return direction === 'desc' ? 1 : -1;
  return direction === 'desc' ? a - b : b - a;
};

export const sortByExist = (a?: boolean, b?: boolean, direction?: SortType) => {
  if (a === undefined) return direction === 'asc' ? 1 : -1;
  if (b === undefined) return direction === 'asc' ? -1 : 1;

  if (direction === 'asc') {
    return a === b ? 0 : a ? 1 : -1;
  } else {
    return a === b ? 0 : a ? -1 : 1;
  }
};

export const getSortField = (json?: string) => {
  if (!json) return undefined;
  const sortField = Object.keys(JSON.parse(json))[0];
  return sortField;
};

export const getSortValue = (json?: string) => {
  if (!json) return undefined;
  const sortValue = Object.values(JSON.parse(json))[0];
  return sortValue as SortType;
};

export const getTableId = (id?: string) => (id ? `${tableSettingsKey}-${id}` : '');

export const getSortOrder = (tableId?: string) => {
  if (!tableId) return [];
  const sortExample = localStorage.getItem(`${tableId}-sort`);
  let sortOrder: string[] = [];
  if (sortExample) {
    sortOrder = JSON.parse(sortExample);
  }

  return sortOrder;
};

export const getKeysToHide = (tableId?: string) => {
  if (!tableId) return [];
  const settings = localStorage.getItem(tableId);
  const parsedSettings = settings ? JSON.parse(settings) : {};
  const keysToHide = Object.keys(parsedSettings).filter((key) => !parsedSettings[key]);
  return keysToHide;
};

export const filterByLangValue = (data?: LangType, value?: string): boolean => {
  const lang = i18n.language as LangEnum;
  const dataValue = data?.[lang] || data?.[getDefaultLang()];
  return !!dataValue?.toLowerCase().includes((value || '').toLowerCase());
};

export const filterByName = (a?: string, b?: string) => {
  return (a || '').toLowerCase().includes((b || '').toLowerCase());
};

export const adjustDateIfNoTime = (
  date: dayjs.Dayjs,
  isTimeSpecified: boolean,
  isEndOfDay = false,
): dayjs.Dayjs => {
  if (!isTimeSpecified) {
    return isEndOfDay ? date.endOf('day') : date.startOf('day');
  }
  return date;
};

export const filterByDateRange = <T>(data: T[], dates: string, filterKey: keyof T): T[] => {
  try {
    const parsedDates = JSON.parse(dates);

    const fromDate = parsedDates.from ? dayjs(parsedDates.from) : null;
    const toDate = parsedDates.to ? dayjs(parsedDates.to) : null;

    const isFromTimeSpecified = !parsedDates.from?.includes('00:00:000Z');
    const isToTimeSpecified = !parsedDates.to?.includes('00:00:000Z');

    const adjustedFromDate = fromDate ? adjustDateIfNoTime(fromDate, isFromTimeSpecified) : null;
    const adjustedToDate = toDate ? adjustDateIfNoTime(toDate, !isToTimeSpecified, true) : null;

    return data.filter((item) => {
      const itemDate = dayjs(item[filterKey] as string);

      if (adjustedFromDate && adjustedToDate) {
        return itemDate.isBetween(adjustedFromDate, adjustedToDate, null, '[]');
      } else if (adjustedFromDate) {
        return itemDate.isSame(adjustedFromDate) || itemDate.isAfter(adjustedFromDate);
      } else if (adjustedToDate) {
        return itemDate.isSame(adjustedToDate) || itemDate.isBefore(adjustedToDate);
      }

      return true;
    });
  } catch (error) {
    console.error("Error parsing 'dates':", error);
  }
  return data;
};

export const filterByTimeRange = (date: string, value?: string) => {
  if (!value) return true;
  const range = JSON.parse(value);
  const from = range.from;
  const to = range.to;

  const dateMinutes = dayjs(date).get('hour') * 60 + dayjs(date).get('minute');
  const fromMinutes = from ? Number(from.split(':')[0]) * 60 + Number(from.split(':')[1]) : 0;
  const toMinutes = to ? Number(to.split(':')[0]) * 60 + Number(to.split(':')[1]) : 24 * 60;

  return dateMinutes >= fromMinutes && dateMinutes <= toMinutes;
};

export const filterByRange = (item: number, value?: string): boolean => {
  if (!value) return true;
  const range = JSON.parse(value);

  if (range.from && range.to) {
    return item >= Number(range.from) && item <= Number(range.to);
  } else if (range.from) {
    return item >= Number(range.from);
  } else if (range.to) {
    return item <= Number(range.to);
  }

  return true;
};
